import React, { useState } from "react";
import {
  DashboardComponentAlign,
  DashboardComponentGroup,
  DashboardComponentJustify,
} from "api/model/dashboard/DashboardComponentGroup";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import useDashboardStore from "store/dashboard-store";
import { ProgressSpinner } from "primereact/progressspinner";
import { DashboardLayoutType } from "api/model/dashboard/DashboardComponent";
import { getDefaultLayout } from "../layouts/helpers";
import { updateDashboardTabs } from "../../utils";

interface GroupConfiguratorProps {
  group: DashboardComponentGroup;
  isDialogVisible: boolean;
  setIsDialogVisible: (isDialogVisible: boolean) => void;
}

const layoutOptions = [
  { label: "Vertical", value: DashboardLayoutType.VERTICAL },
  { label: "Horizontal Flex", value: DashboardLayoutType.HORIZONTAL_FLEX },
];

const justifyOptions = [
  { label: "Start", value: DashboardComponentJustify.START },
  { label: "Center", value: DashboardComponentJustify.CENTER },
  { label: "End", value: DashboardComponentJustify.END },
  { label: "Space Between", value: DashboardComponentJustify.BETWEEN },
  { label: "Space Around", value: DashboardComponentJustify.AROUND },
  { label: "Space Evenly", value: DashboardComponentJustify.EVENLY },
];

const alignOptions = [
  { label: "Start", value: DashboardComponentAlign.START },
  { label: "Center", value: DashboardComponentAlign.CENTER },
  { label: "End", value: DashboardComponentAlign.END },
  { label: "Stretch", value: DashboardComponentAlign.STRETCH },
];

const GroupConfigurator = ({
  group,
  isDialogVisible,
  setIsDialogVisible,
}: GroupConfiguratorProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const [localGroup, setLocalGroup] = useState<DashboardComponentGroup>({ ...group });

  if (!dashboard) {
    return <ProgressSpinner />;
  }

  const handleInputChange = (
    field: string,
    value: DashboardComponentAlign | DashboardComponentJustify | DashboardLayoutType
  ) => {
    setLocalGroup(prevGroup => ({
      ...prevGroup,
      layout: {
        ...prevGroup.layout,
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    updateDashboardTabs(
      dashboard,
      dashboard.dashboardTabsJs?.map(tab => {
        return {
          ...tab,
          componentGroups: tab.componentGroups.map(g => {
            if (g.id === localGroup.id) {
              const layoutTypeChanged = g.layout.type !== localGroup.layout.type;

              const updatedComponents = g.components.map(component => {
                if (layoutTypeChanged) {
                  return {
                    ...component,
                    layout: getDefaultLayout(localGroup.layout.type),
                  };
                }
                return component;
              });

              return {
                ...g,
                components: updatedComponents,
                layout: localGroup.layout,
              };
            }
            return g;
          }),
        };
      })
    );

    setIsDialogVisible(false);
  };

  const handleConfiguratorFooter = () => {
    return (
      <>
        <Button
          tooltip="Cancel"
          label="Cancel"
          className="p-button-danger"
          onClick={() => setIsDialogVisible(false)}
        />
        <Button tooltip="Save" label="Save" className="p-button-success" onClick={handleSave} />
      </>
    );
  };

  const renderInputField = (
    id: string,
    label: string,
    value: any,
    options: any[] | undefined,
    onChange: (e: any) => void,
    inputType: "dropdown" | "inputnumber",
    disabled: boolean
  ) => {
    return (
      <div className="w-full gap-2 flex flex-column align-items-left">
        <label htmlFor={id}>{label}</label>
        {inputType === "dropdown" ? (
          <Dropdown
            id={id}
            className="w-full"
            value={value}
            options={options}
            onChange={onChange}
            disabled={disabled}
          />
        ) : (
          <InputNumber
            id={id}
            className="w-full"
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
        )}
      </div>
    );
  };

  return (
    <Dialog
      header="Group Configurator"
      visible={isDialogVisible}
      onHide={() => setIsDialogVisible(false)}
      draggable={false}
      blockScroll
      style={{ width: "500px" }}
      footer={handleConfiguratorFooter}
    >
      <div className="flex flex-column gap-4">
        <div className="w-full">
          {renderInputField(
            "type",
            "Layout Type",
            localGroup.layout.type,
            layoutOptions,
            e => handleInputChange("type", e.value),
            "dropdown",
            false
          )}
        </div>
        <div className="flex flex-row gap-4 justify-content-between">
          <div className="flex flex-column gap-4">
            {renderInputField(
              "align",
              "Align",
              localGroup.layout.align,
              alignOptions,
              e => handleInputChange("align", e.value),
              "dropdown",
              false
            )}
          </div>
          <div className="flex flex-column gap-4">
            {renderInputField(
              "justify",
              "Justify",
              localGroup.layout.justify,
              justifyOptions,
              e => handleInputChange("justify", e.value),
              "dropdown",
                localGroup.layout.type === DashboardLayoutType.VERTICAL
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default GroupConfigurator;
